import { useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import {
  approveWorkOrderAmendment,
  rejectWorkOrderAmendment,
} from '#/api/momMgmt/workOrder/amendment';
import { PERM } from '#/config/permissions';
import { ROUTES } from '#/config/routes';
import { cn } from '#/lib/utils';
import { Button } from '#/modules/commonComponents/button';
import { AppDialog, useConfirmDialog } from '#/modules/commonComponents/dialog';
import {
  dialogContentCompactGapClassName,
  dialogWideMaxWidthClassName,
} from '#/modules/commonComponents/dialog/dialogLayoutTokens';
import { DialogFooter } from '#/modules/commonComponents/dialog/DialogPrimitives';
import MaterialNameStack from '#/modules/imsMgmt/receipts/shared/MaterialNameStack';
import DocumentRejectReasonDialog from '#/modules/imsMgmt/receipts/shared/DocumentRejectReasonDialog';
import { DESTRUCTIVE_SOLID_BUTTON_CLASS } from '#/modules/imsMgmt/receipts/shared/destructiveSolidButtonClass';
import { useCan } from '#/modules/infra/auth/useCan';
import WorkOrderAmendmentStatusBadge from './WorkOrderAmendmentStatusBadge';
import {
  getWorkOrderApiErrorMessage,
  isWorkOrderVersionConflict,
} from '#/modules/momMgmt/workOrder/helper/apiError';
import {
  canApproveWorkOrderAmendment,
  canEditWorkOrderAmendmentDraft,
  canRejectWorkOrderAmendment,
  pickAmendmentUpdatedAt,
} from '#/modules/momMgmt/workOrder/helper/amendmentStatus';
import { formatDateTimeOrDash } from '#/utils/date';
import { formatDisplayAmount, formatDisplayQty } from '#/utils/formatNumber';
import { toast } from '#/utils/toast';
import {
  workOrderAmendmentDetailQueryKey,
  workOrderAmendmentListQueryKeyPrefix,
  workOrderDetailQueryKey,
  workOrderListQueryKeyPrefix,
} from '#/utils/queryKeys';

function dash(value) {
  if (value === null || value === undefined || value === '') {
    return '—';
  }
  return value;
}

function pickMtr(row, nestedKey, idKey) {
  const nested = row?.[nestedKey];
  if (nested && typeof nested === 'object') {
    return {
      id: nested.id ?? row?.[idKey] ?? null,
      name: nested.name ?? '',
      code: nested.mtrNo ?? nested.code ?? '',
    };
  }
  return {
    id: row?.[idKey] ?? null,
    name: '',
    code: '',
  };
}

function mtrDetailPath(id) {
  return id ? ROUTES.mtrDetail(id) : null;
}

function SummaryCell({ label, value, className }) {
  return (
    <div className={className}>
      <p className="text-xs text-gray-500">{label}</p>
      <p className="mt-0.5 text-sm font-medium wrap-break-word text-gray-900">
        {dash(value)}
      </p>
    </div>
  );
}

SummaryCell.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  className: PropTypes.string,
};

function SubstitutionRow({ row, index }) {
  const from = pickMtr(row, 'fromMtr', 'fromMtrId');
  const to = pickMtr(row, 'toMtr', 'toMtrId');
  const sourceGroup = pickMtr(row, 'sourceGroupMtr', 'sourceGroupMtrId');
  const hasSourceGroup = Boolean(sourceGroup.id || sourceGroup.name || sourceGroup.code);
  const remark = row?.remark;

  return (
    <div
      className={cn(
        'px-3 py-3',
        'bg-gray-50 border border-gray-200',
        'flex flex-col gap-2',
        'rounded-md',
      )}
    >
      <div className="flex flex-wrap items-start gap-3">
        <MaterialNameStack
          name={from.name}
          code={from.code}
          detailPath={mtrDetailPath(from.id)}
        />
        <span className="pt-1 text-sm text-gray-500" aria-hidden>
          →
        </span>
        <MaterialNameStack
          name={to.name}
          code={to.code}
          detailPath={mtrDetailPath(to.id)}
        />
      </div>
      {hasSourceGroup ? (
        <div>
          <p className="text-xs text-gray-500">群組料</p>
          <MaterialNameStack
            name={sourceGroup.name}
            code={sourceGroup.code}
            detailPath={mtrDetailPath(sourceGroup.id)}
          />
        </div>
      ) : null}
      {remark ? (
        <p className="text-gray-600 text-xs wrap-break-word">{remark}</p>
      ) : null}
      <span className="sr-only">{`替代料 ${index + 1}`}</span>
    </div>
  );
}

SubstitutionRow.propTypes = {
  row: PropTypes.object.isRequired,
  index: PropTypes.number.isRequired,
};

function SnapshotBlock({ title, snapshot }) {
  const substitutions = snapshot?.mtrSubstitutions ?? [];

  return (
    <div
      className={cn(
        'px-3 py-3',
        'border border-gray-200',
        'flex flex-col gap-3',
        'rounded-md',
      )}
    >
      <h3 className="text-sm font-medium text-gray-900">{title}</h3>
      <div className="grid grid-cols-2 gap-3">
        <SummaryCell label="報廢量" value={formatDisplayQty(snapshot?.scrapQty)} />
        <SummaryCell label="取消量" value={formatDisplayQty(snapshot?.cancelQty)} />
      </div>
      <div>
        <p className="mb-2 text-xs text-gray-500">替代料</p>
        {substitutions.length === 0 ? (
          <p className="text-sm text-muted-foreground">無替代料</p>
        ) : (
          <div className="flex flex-col gap-2">
            {substitutions.map((row, index) => (
              <SubstitutionRow
                key={row?.id ?? `sub-${index}`}
                row={row}
                index={index}
              />
            ))}
          </div>
        )}
      </div>
      <div>
        <p className="mb-2 text-xs text-gray-500">費用（TWD）</p>
        {!Array.isArray(snapshot?.expenses) ? (
          // 費用功能上線前的異動單沒有費用快照
          <p className="text-sm text-muted-foreground">無費用快照</p>
        ) : snapshot.expenses.length === 0 ? (
          <p className="text-sm text-muted-foreground">無費用</p>
        ) : (
          <ul className="m-0 flex list-none flex-col gap-1 p-0 text-sm">
            {[...snapshot.expenses]
              .sort((left, right) => (left?.displayOrder ?? 0) - (right?.displayOrder ?? 0))
              .map((row, index) => (
                <li key={row?.id ?? `expense-${index}`} className="flex justify-between gap-3">
                  <span className="min-w-0 truncate text-gray-800">{dash(row?.name)}</span>
                  <span className="shrink-0 tabular-nums text-gray-800">
                    {formatDisplayAmount(row?.amount)}
                  </span>
                </li>
              ))}
          </ul>
        )}
      </div>
    </div>
  );
}

SnapshotBlock.propTypes = {
  title: PropTypes.string.isRequired,
  snapshot: PropTypes.shape({
    scrapQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cancelQty: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    mtrSubstitutions: PropTypes.arrayOf(PropTypes.object),
    expenses: PropTypes.arrayOf(PropTypes.object),
  }),
};

/**
 * 工單異動單預覽：before／after；SUBMITTED 可核准／退回，DRAFT 可進編輯。
 */
const WorkOrderAmendmentPreviewDialog = ({
  open,
  onOpenChange,
  amendment,
  workOrderId,
  workOrderStatus,
  onMutated,
}) => {
  const queryClient = useQueryClient();
  const { confirm, ConfirmDialogHost } = useConfirmDialog();
  const [rejectOpen, setRejectOpen] = useState(false);
  const canUpdate = useCan(PERM.WORK_ORDER_AMENDMENT_UPDATE);
  const canReview = useCan(PERM.WORK_ORDER_AMENDMENT_REVIEW);

  const amendmentId = amendment?.id;
  const amendmentNo = amendment?.amendmentNo ?? '—';
  const status = amendment?.status;
  const canEditDraft = canUpdate
    && canEditWorkOrderAmendmentDraft(amendment, workOrderStatus);
  const canApprove = canReview
    && canApproveWorkOrderAmendment(amendment, workOrderStatus);
  const canReject = canReview
    && canRejectWorkOrderAmendment(amendment, workOrderStatus);

  const mutation = useMutation({
    mutationFn: async ({ action, rejectReason }) => {
      const updatedAt = pickAmendmentUpdatedAt(amendment);
      if (!workOrderId || !amendmentId) {
        throw new Error('缺少工單或異動單 id');
      }
      if (!updatedAt) {
        throw new Error('缺少更新時間，請重新載入後再試');
      }
      if (action === 'approve') {
        return approveWorkOrderAmendment(workOrderId, amendmentId, { updatedAt });
      }
      if (action === 'reject') {
        return rejectWorkOrderAmendment(workOrderId, amendmentId, {
          updatedAt,
          rejectReason,
        });
      }
      throw new Error(`Unknown action: ${action}`);
    },
    onSuccess: async (result, variables) => {
      toast.success(result?.message || '操作成功');
      if (variables.action === 'reject') {
        setRejectOpen(false);
      }
      await queryClient.invalidateQueries({ queryKey: workOrderListQueryKeyPrefix() });
      await queryClient.invalidateQueries({ queryKey: workOrderDetailQueryKey(workOrderId) });
      await queryClient.invalidateQueries({
        queryKey: workOrderAmendmentListQueryKeyPrefix(workOrderId),
      });
      await queryClient.invalidateQueries({
        queryKey: workOrderAmendmentDetailQueryKey(workOrderId, amendmentId),
      });
      await onMutated?.();
    },
    onError: (error) => {
      toast.error(getWorkOrderApiErrorMessage(error));
      if (isWorkOrderVersionConflict(error)) {
        onMutated?.();
      }
    },
  });

  const isPending = mutation.isPending;

  async function handleApprove() {
    const ok = await confirm({
      title: '核准異動單',
      message: '核准後將更新工單報廢／取消／替代料，確定核准？',
      confirmText: '核准',
    });
    if (!ok) {
      return;
    }
    mutation.mutate({ action: 'approve' });
  }

  function handleRejectConfirm(rejectReason) {
    mutation.mutate({ action: 'reject', rejectReason });
  }

  const description = (
    <div className="flex flex-wrap items-center gap-2">
      <span className="truncate text-sm font-medium">{amendmentNo}</span>
      {status ? <WorkOrderAmendmentStatusBadge status={status} size="sm" /> : null}
    </div>
  );

  const footer = (
    <DialogFooter>
      <Button
        type="button"
        variant="outline"
        disabled={isPending}
        onClick={() => onOpenChange(false)}
      >
        關閉
      </Button>
      {canEditDraft && workOrderId && amendmentId ? (
        <Button type="button" variant="outline" asChild>
          <Link to={ROUTES.workOrderAmendmentEdit(workOrderId, amendmentId)}>
            編輯
          </Link>
        </Button>
      ) : null}
      {canReject ? (
        <Button
          type="button"
          variant="destructive"
          className={cn(DESTRUCTIVE_SOLID_BUTTON_CLASS)}
          disabled={isPending}
          onClick={() => setRejectOpen(true)}
        >
          退回
        </Button>
      ) : null}
      {canApprove ? (
        <Button type="button" disabled={isPending} onClick={handleApprove}>
          核准
        </Button>
      ) : null}
    </DialogFooter>
  );

  return (
    <>
      {ConfirmDialogHost}
      <DocumentRejectReasonDialog
        open={rejectOpen}
        onOpenChange={setRejectOpen}
        title="退回異動單"
        description="退回後異動單將標記為已退回，確定退回？"
        confirmText="退回"
        isPending={isPending}
        onConfirm={handleRejectConfirm}
      />
      <AppDialog
        open={open}
        onOpenChange={(nextOpen) => {
          if (!nextOpen && isPending) {
            return;
          }
          onOpenChange(nextOpen);
        }}
        title="異動單預覽"
        description={description}
        headerVariant="primary"
        showCloseButton
        maxWidthClassName={dialogWideMaxWidthClassName}
        contentClassName={dialogContentCompactGapClassName}
        closeOnOutsideClick={!isPending}
        closeOnEscape={!isPending}
        footer={footer}
      >
        {amendment ? (
          <div className="space-y-4">
            <div
              className={cn(
                'grid grid-cols-1 gap-x-4 gap-y-3',
                'sm:grid-cols-2',
              )}
            >
              <SummaryCell
                label="事由"
                value={amendment.issueReason}
                className="col-span-1 sm:col-span-2"
              />
              <SummaryCell
                label="建立時間"
                value={formatDateTimeOrDash(amendment.record?.createdAt ?? amendment.createdAt)}
              />
              <SummaryCell
                label="核准時間"
                value={formatDateTimeOrDash(amendment.history?.approvedAt)}
              />
            </div>
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <SnapshotBlock title="變更前" snapshot={amendment.before} />
              <SnapshotBlock title="變更後" snapshot={amendment.after} />
            </div>
          </div>
        ) : null}
      </AppDialog>
    </>
  );
};

WorkOrderAmendmentPreviewDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  workOrderId: PropTypes.string,
  workOrderStatus: PropTypes.string,
  onMutated: PropTypes.func,
  amendment: PropTypes.shape({
    id: PropTypes.string,
    amendmentNo: PropTypes.string,
    status: PropTypes.string,
    issueReason: PropTypes.string,
    createdAt: PropTypes.string,
    updatedAt: PropTypes.string,
    record: PropTypes.shape({
      createdAt: PropTypes.string,
      updatedAt: PropTypes.string,
    }),
    history: PropTypes.shape({
      approvedAt: PropTypes.string,
    }),
    before: PropTypes.object,
    after: PropTypes.object,
  }),
};

export default WorkOrderAmendmentPreviewDialog;
